export const DEFAULT_GUIDE_KEY = "organization-context";

export const GUIDE_FLOW_TABS = [
    { id: "organization-context", label: "Organization Context" },
    { id: "product", label: "Product" },
    { id: "search-leads", label: "Search Leads" },
    { id: "connections", label: "Connections" },
    { id: "campaign", label: "Campaign" },
    { id: "track-progress", label: "Track Progress" },
];

export const guidesData = {
    "organization-context": {
        title: "Organization Context",
        shortDescription:
            "This is where you tell OptiMind about your company, business, and positioning to understand your outreach context.",
        whatIsIt: {
            description:
                "Organization Context stores the core company information OptiMind uses across your workspace.",
            items: [
                "Company Description",
                "Brand Voice",
                "Target ICP",
                "Core Value Proposition",
                "Website URL",
            ],
        },
        whyYouNeedIt: {
            description:
                "OptiMind needs this information to understand your business before it supports workflows like lead search and campaign creation.",
            items: [
                "Organization Context must be created before Lead Search becomes available.",
                "It helps keep lead search and campaign context aligned with your business.",
            ],
        },
        whenToUseIt:
            "Create Organization Context when you first Sign Up in OptiMind. Update it later whenever your company positioning, target audience, brand voice, or company details change.",
        steps: [
            {
                title: "Open Organization Context",
                description:
                    "Go to the Organization Context page from the profile menu.",
            },
            {
                title: "Add Company Description",
                description:
                    "Describe what your company does in clear, useful language.",
            },
            {
                title: "Define Brand Voice",
                description:
                    "Explain how your outreach should sound when communicating with leads.",
            },
            {
                title: "Describe Target ICP",
                description:
                    "Add the type of customers, buyers, or companies you want to reach.",
            },
            {
                title: "Add Core Value Proposition",
                description:
                    "Summarize the main value your company or product gives to customers.",
            },
            {
                title: "Add Website URL",
                description:
                    "Provide your company website so the context includes your public business presence.",
            },
            {
                title: "Save Organization Context",
                description:
                    "Save the form so OptiMind can unlock and support the next workflows.",
            },
        ],
        importantNotes: [
            "Organization Context is required before Lead Search.",
            "Information should be accurate and meaningful.",
            "You can update Organization Context later.",
            "Existing form validation and minimum input requirements still apply.",
        ],
        video: {
            title: "How to Set Up Organization Context",
            placeholder: "Video tutorial coming soon",
            url: "https://www.youtube.com/watch?v=p9r_SLTfhYo",
            thumbnailUrl: "/guides/Organization Context Guide Thumbnail.webp",
        },
        nextStep: {
            description:
                "After completing Organization Context, add a product so OptiMind can connect your business context with a specific offer.",
            label: "Add a Product",
            guideKey: "product",
        },
    },
    product: {
        title: "Product",
        shortDescription:
            "Products help OptiMind understand what you are promoting before you create outreach campaigns.",
        whatIsIt: {
            description:
                "A Product represents the offer, service, or solution you want to use in outreach.",
            items: [
                "Create products with clear name and description details.",
                "Select a product to view and manage its details.",
                "Edit product information when positioning changes.",
                "Review campaigns that use a selected product when available.",
            ],
        },
        whyYouNeedIt: {
            description:
                "Campaigns need product context so the outreach message is tied to the right offer.",
            items: [
                "Product details help shape campaign context.",
                "A selected or newly created product is required during campaign creation.",
            ],
        },
        whenToUseIt:
            "Use Product after Organization Context is ready and before creating a campaign for a specific offer.",
        steps: [
            {
                title: "Open Products",
                description:
                    "Go to the Products page from the navbar, dashboard, or page actions.",
            },
            {
                title: "Create a product",
                description:
                    "Add the product name and description so OptiMind has product context.",
            },
            {
                title: "Select a product",
                description:
                    "Choose a product from the list to view its details.",
            },
            {
                title: "Review product details",
                description:
                    "Check the saved product information before using it in campaigns.",
            },
            {
                title: "Edit when needed",
                description:
                    "Update product information when your offer or positioning changes.",
            },
            {
                title: "Initiate a campaign",
                description:
                    "Use a selected product with a completed search to start campaign creation.",
            },
        ],
        importantNotes: [
            "Product name and description must meet the existing minimum input requirements.",
            "Campaign creation from a product requires at least one active sender connection.",
            "A completed search is needed before a product can be used to create a campaign.",
        ],
        video: {
            title: "How to Add and Manage Products",
            placeholder: "Video tutorial coming soon",
            url: "https://www.youtube.com/watch?v=wA9fSHl196c",
            thumbnailUrl: "/guides/Product Guide Tumbnail.webp",
        },
        nextStep: {
            description:
                "After adding a product, search for leads that match the audience you want to reach.",
            label: "Search Leads",
            guideKey: "search-leads",
        },
    },
    "search-leads": {
        title: "Search Leads",
        shortDescription:
            "Search Leads lets you enter a plain-English query and find leads that can later be used for campaigns.",
        whatIsIt: {
            description:
                "Lead Search turns your search query into lead results and saved search history.",
            items: [
                "Enter plain-English lead search queries.",
                "Wait while searches process when results are not ready immediately.",
                "View lead results in a scrollable results list.",
                "Reuse previous completed searches from recent searches or history.",
                "Export CSV only when your plan or account permission allows it.",
            ],
        },
        whyYouNeedIt: {
            description:
                "Campaign creation starts from a completed search, so lead search helps you build the audience for outreach.",
            items: [
                "Organization Context must exist before the search workspace is available.",
                "Available search credits are checked before search submission.",
            ],
        },
        whenToUseIt:
            "Use Search Leads after Organization Context is complete and you are ready to find target customers for your product or campaign.",
        steps: [
            {
                title: "Open Search",
                description: "Go to the Search page from the navbar or actions.",
            },
            {
                title: "Check Organization Context",
                description:
                    "If context is missing, complete the inline Organization Context form first.",
            },
            {
                title: "Enter a search query",
                description:
                    "Describe the leads you want in plain English.",
            },
            {
                title: "Run the search",
                description:
                    "Submit the query and wait if OptiMind needs time to prepare results.",
            },
            {
                title: "Review lead results",
                description:
                    "Use the results list to inspect available leads from the search.",
            },
            {
                title: "Use completed searches",
                description:
                    "Create or view campaigns from searches that are completed.",
            },
        ],
        importantNotes: [
            "Organization Context is required before Lead Search.",
            "Searches require available search credits.",
            "Processing searches are not ready for campaign creation yet.",
            "CSV export is available only when the user's plan or account permission allows it.",
        ],
        video: {
            title: "How to Search for Leads",
            placeholder: "Video tutorial coming soon",
            url: "https://www.youtube.com/watch?v=bUgYJgwPJfM",
            thumbnailUrl: "/guides/Search Lead Guide Thumbnail.webp",
        },
        nextStep: {
            description:
                "After finding leads, create or confirm a sender connection so campaigns can send outreach.",
            label: "Connections",
            guideKey: "connections",
        },
    },
    connections: {
        title: "Connections",
        shortDescription:
            "Connections are sender accounts used in outreach campaigns to send messages to leads.",
        whatIsIt: {
            description:
                "The Connections page helps you add and manage sender accounts.",
            items: [
                "Connect a Google sender account.",
                "Or create an SMTP sender connection.",
                "Test connected accounts.",
                "Set a primary sender connection.",
                "Manage connected accounts from one list.",
            ],
        },
        whyYouNeedIt: {
            description:
                "OptiMind needs an active sender connection before it can launch outreach campaigns.",
            items: [
                "Campaign creation requires at least one active sender connection.",
                "A primary connection helps identify the preferred sender account.",
            ],
        },
        whenToUseIt:
            "Use Connections before creating campaigns, or whenever you need to add, test, change, or remove sender accounts.",
        steps: [
            {
                title: "Open Connections",
                description: "Go to the Connections page from the profile menu.",
            },
            {
                title: "Choose a connection method",
                description: "Select Google or SMTP based on your sender setup.",
            },
            {
                title: "Add the sender account",
                description:
                    "Complete the connection flow or SMTP form details.",
            },
            {
                title: "Test the connection",
                description:
                    "Use the account menu to confirm the sender connection works.",
            },
            {
                title: "Set a primary sender",
                description:
                    "Set the account as primary you want OptiMind to use as the main sender.",
            },
            {
                title: "Manage accounts",
                description:
                    "Review connected accounts and remove accounts when allowed.",
            },
        ],
        importantNotes: [
            "At least one active sender connection is required before launching a campaign.",
            "Google OAuth and SMTP are the available connection methods.",
            "Deleting a connection can be blocked when it would remove the last usable or primary sender.",
        ],
        video: {
            title: "How to Create a Sender Connection",
            placeholder: "Video tutorial coming soon",
            url: "https://www.youtube.com/watch?v=NWfZzZO4LMM",
            thumbnailUrl: "/guides/Connections Guide Thumbnail.webp",
        },
        nextStep: {
            description:
                "After a sender connection is ready, create a campaign from a completed search and product.",
            label: "Campaign",
            guideKey: "campaign",
        },
    },
    campaign: {
        title: "Campaign",
        shortDescription:
            "Campaigns turn a completed search, selected product, and sender connection into a structured outreach sequence.",
        whatIsIt: {
            description:
                "Campaign creation guides you through product context, campaign setup, timeline review, and launch.",
            items: [
                "Choose a completed search.",
                "Choose or create a product.",
                "Design campaign context.",
                "Review the generated campaign timeline.",
                "Approve and start the campaign when ready.",
            ],
        },
        whyYouNeedIt: {
            description:
                "Campaigns organize outreach into clear steps so leads from a search can be contacted with the right product context.",
            items: [
                "The campaign flow connects leads, product context, and sender setup.",
                "Campaign guards and limits help prevent invalid launches.",
            ],
        },
        whenToUseIt:
            "Use Campaign after you have Organization Context, a product, a completed search, and an active sender connection.",
        steps: [
            {
                title: "Start from a completed search",
                description:
                    "Open campaign creation from Search, Product, Dashboard, or the Campaign page.",
            },
            {
                title: "Choose or create a product",
                description:
                    "Select the product context for the campaign if it is not already selected.",
            },
            {
                title: "Design campaign details",
                description:
                    "Add the campaign setup and context fields required by the modal.",
            },
            {
                title: "Generate the preview",
                description:
                    "Let OptiMind prepare the campaign blueprint and timeline.",
            },
            {
                title: "Review the timeline",
                description:
                    "Check campaign steps and adjust allowed timeline values before launch.",
            },
            {
                title: "Approve and start",
                description:
                    "Start the campaign after reviewing the final preview.",
            },
        ],
        importantNotes: [
            "Only completed searches can be used for campaign creation.",
            "At least one active sender connection is required.",
            "Campaign setup fields must meet existing validation requirements.",
            "Timeline delays must follow the existing campaign step rules.",
            "Campaign limits or guards may prevent launch when limits are reached.",
        ],
        video: {
            title: "How to Create a Campaign",
            placeholder: "Video tutorial coming soon",
            url: "https://www.youtube.com/watch?v=MxkpwvSBujk",
            thumbnailUrl: "/guides/Campaigns Guide Thumbnail.webp",
        },
        nextStep: {
            description:
                "After launching a campaign, monitor its status, details, and progress from the Campaign page.",
            label: "Track Progress",
            guideKey: "track-progress",
        },
    },
    "track-progress": {
        title: "Track Progress",
        shortDescription:
            "Track Progress helps you monitor campaigns after they are created and manage campaign actions where available.",
        whatIsIt: {
            description:
                "The Campaign page shows campaign groups, statuses, metrics, details, and management actions.",
            items: [
                "View campaigns and campaign groups.",
                "See campaign status and metrics.",
                "Open campaign details.",
                "Pause or resume campaigns where allowed.",
                "Edit campaign context where allowed.",
                "Filter and search campaigns.",
            ],
        },
        whyYouNeedIt: {
            description:
                "Tracking progress helps you understand what is happening after a campaign starts.",
            items: [
                "Campaign status and metrics show campaign health.",
                "Details help you inspect campaign context and timeline.",
                "Filters help you find the campaign you need faster.",
            ],
        },
        whenToUseIt:
            "Use Track Progress after campaigns are created, or whenever you need to inspect, filter, pause, resume, or review outreach activity.",
        steps: [
            {
                title: "Open Campaigns",
                description:
                    "Go to the Campaign page to view campaign activity.",
            },
            {
                title: "Review campaign stats",
                description:
                    "Use the summary cards to understand campaign counts and status.",
            },
            {
                title: "Find a campaign",
                description:
                    "Use search, filters, or campaign groups to locate the campaign.",
            },
            {
                title: "View campaign details",
                description:
                    "Open details to inspect campaign context, steps, and related information.",
            },
            {
                title: "Manage allowed actions",
                description:
                    "Pause, resume, or edit campaign context when the campaign status allows it.",
            },
            {
                title: "Keep monitoring",
                description:
                    "Return to the Campaign page as campaigns continue progressing.",
            },
        ],
        importantNotes: [
            "Available actions depend on campaign status.",
            "Campaigns can also be inspected from related searches or products where applicable.",
            "Filtering and search do not change campaign data; they only adjust what is visible.",
        ],
        nextStep: {
            description:
                "This is the final step in the guide flow. Continue monitoring and managing campaigns as your outreach progresses.",
            items: ["Keep reviewing campaign status, details, and allowed actions."],
        },
    },
};
